import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock,
  Users,
  MessageSquare,
  TrendingUp,
  Upload,
  Filter,
  Download,
  Search,
  X,
  Edit2,
  Clock4,
} from 'lucide-react';
import { useTranscriptStore } from '@/store';
import { calculateStats, calculateStatsFromSegments, formatDuration } from '@/utils/parser';
import { Timeline } from './Timeline';
import { SpeakerFilter } from './SpeakerFilter';
import { SegmentList } from './SegmentList';
import { StatsCards } from './StatsCards';
import { SpeakerPieChart } from './SpeakerPieChart';
import { SpeakerEditor } from './SpeakerEditor';
import { useState } from 'react';

export function Dashboard() {
  const navigate = useNavigate();
  const transcript = useTranscriptStore((state) => state.transcript);
  const filters = useTranscriptStore((state) => state.filters);
  const showAbsoluteTime = useTranscriptStore((state) => state.showAbsoluteTime);
  const toggleAbsoluteTime = useTranscriptStore((state) => state.toggleAbsoluteTime);
  const setSearchQuery = useTranscriptStore((state) => state.setSearchQuery);
  const clearFilters = useTranscriptStore((state) => state.clearFilters);
  const getFilteredSegments = useTranscriptStore((state) => state.getFilteredSegments);
  
  const [showFilters, setShowFilters] = useState(false);

  // Empty state
  if (!transcript) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-blue-200 dark:from-sky-900 dark:to-blue-800 rounded-3xl flex items-center justify-center mx-auto">
            <MessageSquare className="w-10 h-10 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              No Transcript Loaded
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-md">
              Import a transcript to visualize speaker timeline, analytics, and more.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/upload')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-sky-200 dark:shadow-sky-900/30"
          >
            <Upload className="w-5 h-5" />
            Import Transcript
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const filteredSegments = getFilteredSegments();
  const hasActiveFilters = filters.selectedSpeakers.length > 0 || filters.searchQuery.trim() !== '';
  
  // Calculate stats based on whether filters are active
  const filteredSpeakers = filters.selectedSpeakers.length > 0 
    ? transcript.speakers.filter(s => filters.selectedSpeakers.includes(s.id))
    : transcript.speakers;
    
  const stats = hasActiveFilters 
    ? calculateStatsFromSegments(filteredSegments, filteredSpeakers, transcript.duration)
    : calculateStats(transcript);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {transcript.title}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 flex items-center gap-2 mt-1">
            <Clock className="w-4 h-4" />
            {new Date(transcript.recordingDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAbsoluteTime}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              showAbsoluteTime
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
            title={showAbsoluteTime ? 'Show relative time' : 'Show absolute time'}
          >
            <Clock4 className="w-4 h-4" />
            {showAbsoluteTime ? 'Absolute' : 'Relative'}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              showFilters || hasActiveFilters
                ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-sky-500 rounded-full" />
            )}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={filters.searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transcript..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none text-slate-900 dark:text-white placeholder-slate-400"
              />
            </div>

            {/* Speaker Filter */}
            <div className="sm:w-64">
              <SpeakerFilter speakers={transcript.speakers} />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {filteredSegments.length} of {transcript.segments.length} segments
              </p>
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Stats Overview */}
      <StatsCards stats={stats} />

      {/* Timeline Visualization */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-sky-500" />
          Speaker Timeline
          {hasActiveFilters && (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-full">
              Filtered
            </span>
          )}
        </h3>
        <Timeline
          segments={filteredSegments}
          speakers={filters.selectedSpeakers.length > 0 
            ? transcript.speakers.filter(s => filters.selectedSpeakers.includes(s.id))
            : transcript.speakers}
          duration={transcript.duration}
          recordingDate={transcript.recordingDate}
          showAbsoluteTime={showAbsoluteTime}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Speaker Distribution */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-500" />
            Speaker Distribution
            {hasActiveFilters && (
              <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-full">
                Filtered
              </span>
            )}
          </h3>
          <SpeakerPieChart speakerStats={stats.speakerStats} />
        </div>

        {/* Speaker Editor */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-sky-500" />
            Edit Speakers
          </h3>
          <SpeakerEditor speakers={transcript.speakers} />
          
          {/* Quick Stats below editor */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
              Stats
            </p>
            <div className="space-y-2">
              {stats.speakerStats.map((speaker) => (
                <div
                  key={speaker.speakerId}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: speaker.color }}
                    />
                    <span className="text-slate-600 dark:text-slate-400 truncate max-w-[120px]">
                      {speaker.speakerName}
                    </span>
                  </div>
                  <span className="text-slate-900 dark:text-white font-medium">
                    {speaker.percentage.toFixed(1)}% • {formatDuration(speaker.totalTime)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Segment List */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-sky-500" />
          Transcript Segments
        </h3>
        <SegmentList
          segments={filteredSegments}
          speakers={transcript.speakers}
          recordingDate={transcript.recordingDate}
          showAbsoluteTime={showAbsoluteTime}
        />
      </div>
    </div>
  );
}
